import React, { forwardRef, useContext, useMemo, useState } from 'react';
import { alpha, Box, Button, Stack, Tooltip } from '@mui/material';
import {
  DarkGrey,
  GreenColor,
  RegularText,
  SubtleGrey,
  TextfieldSelctOption,
  TextfieldSelect,
  useWindowDimensions,
} from '@Iot-Bee/standard-web-library';
import { calculatedKwhSensor, ChartContext } from '../context/ChartContext';
import TuneIcon from '@mui/icons-material/Tune';
import Settings from './Settings';
import SideDialog, { Transition } from './SideDialog';

const KwhCalculatedTooltip = forwardRef((props, ref) => {
  return (
    <Box ref={ref}>
      <RegularText sx={{ fontSize: '.8rem', lineHeight: '1.45', color: 'black' }}>
        Data for the past week may be updated as sensors can transmit delayed or backup readings within seven days of
        collection. This process ensures the most accurate representation of your energy consumption.
      </RegularText>
    </Box>
  );
});

const DataFilter = () => {
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const chartContext = useContext(ChartContext);

  if (!chartContext) {
    throw new Error('DataFilter must be used within a ChartContextProvider');
  }

  const {
    availableSensors,
    loadingGraphData,
    chartOptions: { sensor },
    setChartOptions: { handleChangeSensor },
  } = chartContext;

  const { screenwidth } = useWindowDimensions();

  const isDesktop = screenwidth > 768;

  const displaySensorOptions: TextfieldSelctOption[] = useMemo(() => {
    return availableSensors.map((s) => {
      if (s === 'kwh_delta_sum') {
        return {
          displayValue: 'kWh',
          value: 'kwh_delta_sum',
        };
      }
      return s;
    });
  }, [availableSensors]);

  return (
    <Box sx={{ background: SubtleGrey }}>
      <Stack
        direction="row"
        spacing={4}
        sx={
          isDesktop
            ? { px: '20px', pt: '24px', pb: sensor === calculatedKwhSensor ? '' : '24px' }
            : { py: '20px', pl: '20px', display: 'flex', justifyContent: 'space-between' }
        }
      >
        <TextfieldSelect
          label="Sensor"
          placeholder="Select reading"
          options={displaySensorOptions}
          value={sensor ?? ''}
          onChange={handleChangeSensor}
          width="145px"
          disabled={loadingGraphData}
        />
        {isDesktop ? (
          <Settings />
        ) : (
          <Box>
            <Button onClick={() => setMobileFilterOpen(true)}>
              <TuneIcon sx={{ color: DarkGrey }} />
            </Button>
            <SideDialog
              keepMounted
              open={mobileFilterOpen}
              onClose={() => setMobileFilterOpen(false)}
              TransitionComponent={Transition}
            >
              <Stack gap={3} sx={{ p: '20px' }}>
                <Settings />
              </Stack>
            </SideDialog>
          </Box>
        )}
      </Stack>
      {sensor === calculatedKwhSensor && (
        <RegularText sx={{ pl: '20px', color: DarkGrey, textDecoration: 'underline', fontSize: 'small' }}>
          <Tooltip
            title={<KwhCalculatedTooltip />}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: alpha(GreenColor, 0.7),
                  backdropFilter: 'blur(6px)',
                  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                },
              },
            }}
          >
            <span>kWh information</span>
          </Tooltip>
        </RegularText>
      )}
    </Box>
  );
};

export default DataFilter;
